import { Box, TextField, Typography, Tooltip, InputAdornment } from '@mui/material';
import React from 'react';
import { HiOutlineExclamationCircle as ErrorIcon } from 'react-icons/hi';

const RequiredMarker = () => (
    <Box
        component="span"
        sx={{ color: (theme) => theme.palette.error.main }}
    >
        *{' '}
    </Box>
);
const ErrorTooltip = ({ errorMessage }) => {
    return (
        <InputAdornment
            sx={(theme) => {
                return {
                    mr: theme.spacing(1),
                    cursor: 'pointer',
                };
            }}
        >
            <Tooltip
                title={errorMessage}
                placement="top"
                arrow
                sx={(theme) => {
                    return {
                        color: theme.palette.error.main,
                    };
                }}
            >
                <Box>
                    <ErrorIcon />
                </Box>
            </Tooltip>
        </InputAdornment>
    );
};

/** A styled wrapper for the MUI TextField component. Uses a Typography with component of label instead of the default TextField label.
 *
 * @param {boolean} required A boolean property that if present will add the required marker.
 * @param {string} label A text value to display as the label of the input.
 * @param {string} id A unique identifier for linking the label and input.
 * @param {string} errorMessage A message relative to the reason the component is in error. Business does not like the standard helper text, so this is in place of that.
 * @param {object|function} typographySxObj A MUI sx object or callback function to be applied to the Typography component.
 * @param {object|function} textFieldSxObj A MUI sx object or callback function to be applied to the TextField component.
 * @param {object} inputProps A MUI inputProps object that will be applied to the TextField component.
 * @param {} restOfProps Any properties that are valid for the MUI TextField component.
 * @see {@link https://mui.com/material-ui/api/box/ MUI Box API} for further information.
 * @see {@link https://mui.com/material-ui/api/typography/ MUI Typography API} for further information.
 * @see {@link https://mui.com/material-ui/api/text-field/ MUI TextField API} for further information.
 */
const MuiStyledInput = ({ required, label, id, errorMessage, typographySxObj, textFieldSxObj, inputProps, ...restOfProps }) => {
    return (
        <Box sx={restOfProps?.sx}>
            <Box
                sx={(theme) => {
                    return {
                        display: 'flex',
                        flexDirection: 'row',
                        gap: theme.spacing(2),
                    };
                }}
            >
                <Typography
                    htmlFor={id}
                    component="label"
                    sx={(theme) => {
                        let override = typeof typographySxObj === 'function' ? typographySxObj(theme) : typographySxObj;
                        return {
                            display: 'block',
                            color: restOfProps?.error ? theme.palette.error.main : theme.palette.grey.header,
                            fontWeight: 700,
                            fontSize: '0.8125rem',
                            ...override,
                        };
                    }}
                >
                    {required ? <RequiredMarker /> : null}
                    {label}
                </Typography>
            </Box>

            <TextField
                {...restOfProps}
                inputProps={{
                    id,
                    ...inputProps,
                }}
                sx={(theme) => {
                    let override = typeof textFieldSxObj === 'function' ? textFieldSxObj(theme) : textFieldSxObj;
                    return {
                        width: '14rem',
                        [theme.breakpoints.down('xl')]: {
                            width: '10.9rem',
                        },
                        [theme.breakpoints.down('lg')]: {
                            width: '7.8rem',
                        },
                        ...override,
                        '.Mui-disabled': {
                            backgroundColor: theme.palette.grey['disabled-background'],
                            color: '#586874',
                            fontSize: '15px',
                            '> .MuiInputBase-input': {
                                WebkitTextFillColor: 'unset',
                            },
                            '& fieldset': {
                                border: 'none',
                            },
                        },
                        'input::-ms-reveal': {
                            //Hides the Default html masking icon.
                            display: 'none',
                        },
                    };
                }}
                size="small"
                autoComplete="nope"
                InputProps={{
                    ...restOfProps?.InputProps,
                    startAdornment: restOfProps?.error && errorMessage ? <ErrorTooltip errorMessage={errorMessage} /> : restOfProps?.InputProps?.startAdornment,
                }}
            />
        </Box>
    );
};

export default MuiStyledInput;
